import React, { useMemo, useRef, useState } from "react";
import { Box, ButtonBase } from "@mui/material";
import { RiAddLine, RiSubtractLine } from "@remixicon/react";
import { focusRing, tokens } from "../../utils/figmaTokens";
import { ChartTooltip } from "./ChartTooltip";

const STEP = 1.5;
const MAX_K = 5;

const clampCentre = (v, span, total) => Math.min(Math.max(v, span / 2), total - span / 2);

// Reports' Users by country map (044 D90, R34/R35): a plain SVG world map, zoomed with the +/−
// control only (a wheel would fight the page's scroll), with a tooltip that follows the pointer
// or a tap. `data`/`fallback` are `{ fill, value, note }`; the caller has already filtered `map`.
export const WorldMap = ({ map, data, fallback, label }) => {
  const { width: W, height: H, shapes } = map;
  const layer = useMemo(
    () =>
      shapes.map((s) => {
        const hit = data.get(s.code) || fallback;
        return (
          <path
            key={s.code}
            d={s.d}
            data-code={s.code}
            fill={hit.fill}
            stroke={tokens.surface.default}
            strokeWidth={0.75}
            vectorEffect="non-scaling-stroke"
          />
        );
      }),
    [shapes, data, fallback]
  );

  const [k, setK] = useState(0);
  const [centre, setCentre] = useState({ cx: W / 2, cy: H / 2 });
  const [hover, setHover] = useState(null); // { code, x, y }
  const [drag, setDrag] = useState(null); // { startX, startY, startCx, startCy, moved }
  const svgRef = useRef(null);
  const wellRef = useRef(null);

  const scaleFactor = STEP ** k;
  const spanW = W / scaleFactor;
  const spanH = H / scaleFactor;
  const viewBox = `${centre.cx - spanW / 2} ${centre.cy - spanH / 2} ${spanW} ${spanH}`;

  const moveTo = (cx, cy) =>
    setCentre({ cx: clampCentre(cx, spanW, W), cy: clampCentre(cy, spanH, H) });

  const zoom = (dk) => {
    const nk = Math.min(MAX_K, Math.max(0, k + dk));
    if (nk === k) return;
    setK(nk);
    const nSpanW = W / STEP ** nk;
    const nSpanH = H / STEP ** nk;
    setCentre((c) => ({ cx: clampCentre(c.cx, nSpanW, W), cy: clampCentre(c.cy, nSpanH, H) }));
  };

  const scaleOf = () => {
    const box = svgRef.current.getBoundingClientRect();
    return Math.min(box.width / spanW, box.height / spanH);
  };

  const wellPoint = (clientX, clientY) => {
    const box = wellRef.current.getBoundingClientRect();
    return { x: clientX - box.left, y: clientY - box.top };
  };

  const shapeOf = (code) => {
    const hit = data.get(code) || fallback;
    const shape = shapes.find((s) => s.code === code);
    return { name: shape ? shape.name : "", ...hit };
  };

  const onPathMove = (e) => {
    if (drag) return;
    const code = e.target.dataset.code;
    if (!code) {
      setHover(null);
      return;
    }
    const { x, y } = wellPoint(e.clientX, e.clientY);
    setHover({ code, x, y });
  };

  const onSvgLeave = () => {
    if (!drag) setHover(null);
  };

  const onPointerDown = (e) => {
    if (k > 0) e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ startX: e.clientX, startY: e.clientY, startCx: centre.cx, startCy: centre.cy, moved: 0, code: e.target.dataset.code });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (k > 0) {
      const scale = scaleOf();
      moveTo(drag.startCx - dx / scale, drag.startCy - dy / scale);
    }
    setDrag((d) => ({ ...d, moved: Math.max(d.moved, Math.hypot(dx, dy)) }));
  };

  const onPointerUp = (e) => {
    if (!drag) return;
    const { moved, code } = drag;
    setDrag(null);
    if (moved > 3) return; // a drag, not a tap
    if (code) {
      const { x, y } = wellPoint(e.clientX, e.clientY);
      setHover({ code, x, y });
    } else {
      setHover(null);
    }
  };

  const onPointerCancel = () => setDrag(null);

  const tooltip = hover && shapeOf(hover.code);
  const below = hover && hover.y < 100;

  return (
    <Box
      ref={wellRef}
      sx={{
        position: "relative",
        bgcolor: tokens.chart.well,
        borderRadius: "12px",
        height: { xs: 240, sm: 440 },
        p: "16px",
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={label}
        style={{
          display: "block",
          cursor: k > 0 ? (drag ? "grabbing" : "grab") : "default",
          touchAction: k > 0 ? "none" : "auto",
        }}
        onMouseMove={onPathMove}
        onMouseLeave={onSvgLeave}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        {layer}
      </svg>

      {tooltip && <ChartTooltip x={hover.x} y={hover.y} below={below} title={tooltip.name} value={tooltip.value} note={tooltip.note} />}

      <Box
        sx={{
          position: "absolute",
          left: "16px",
          bottom: "16px",
          bgcolor: tokens.surface.default,
          border: `1px solid ${tokens.border.default}`,
          borderRadius: "10px",
          p: "2px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ButtonBase
          onClick={() => zoom(1)}
          disabled={k === MAX_K}
          aria-label="Zoom in"
          sx={{ width: 34, height: 34, borderRadius: "8px", opacity: k === MAX_K ? 0.4 : 1, ...focusRing }}
        >
          <RiAddLine size={18} color={tokens.icon.default} aria-hidden />
        </ButtonBase>
        <Box sx={{ width: 34, height: "1px", bgcolor: tokens.divider }} />
        <ButtonBase
          onClick={() => zoom(-1)}
          disabled={k === 0}
          aria-label="Zoom out"
          sx={{ width: 34, height: 34, borderRadius: "8px", opacity: k === 0 ? 0.4 : 1, ...focusRing }}
        >
          <RiSubtractLine size={18} color={tokens.icon.default} aria-hidden />
        </ButtonBase>
      </Box>
    </Box>
  );
};
